import Modal from "bootstrap/js/dist/modal";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { postPhysicianAppointment } from "services/api/taptype/appointments";
import { apiHandler, isFunction, isObject } from "utils/helpers";
import { default as AppointmentForm } from "views/forms/Appointment";
import { default as HcpShareDocumentModal } from "views/modals/HcpShareDocument";
import { default as TimeOverlappingModal } from "views/modals/TimeOverlapping";

const Appointment = ({
	appointment = null,
	patient = {},
	isSinglePatientView,
	isShown,
	setShown,
	onShowCb,
	setPatientShown,
	onSuccessCb,
	isEdit,
	onDeleteButtonClickHandler,
}) => {
	const { t } = useTranslation();
	const modalId = useId().replace(/:/g, "");

	// portal dom element
	let portalsRoot = document.getElementById("portals") || null;
	if (!portalsRoot) {
		portalsRoot = document.createElement("div");
		portalsRoot.setAttribute("id", "portals");
		document.body.appendChild(portalsRoot);
	}

	// ref hooks
	const modalRef = useRef(null);
	const overlapModalRef = useRef(null);
	const overlapSubmitBodyRef = useRef(null);
	const overlapSubmitRequestRef = useRef(null);

	// state hooks
	const [isTimeOverlapMessageEnabledState, setIsTimeOverlapMessageEnabledState] = useState(false);
	const [timeOverlapMessageState, setTimeOverlapMessageState] = useState("");
	const [isOverlapSubmitLoadingState, setIsOverlapSubmitLoadingState] = useState(false);
	const [isDocumentsShownState, setIsDocumentsShownState] = useState(false);
	const [appointmentState, setAppointmentState] = useState({});
	const [selectedDocuments, setSelectedDocuments] = useState([]);
	const [refetchDocumentsState, setRefetchDocumentsState] = useState(false);

	const hideOverlapModal = useCallback(() => {
		overlapModalRef.current?.hide();
		setIsTimeOverlapMessageEnabledState(false);
		setTimeOverlapMessageState("");
		setIsOverlapSubmitLoadingState(false);
	}, []);

	const hideAppointmentModal = useCallback(() => {
		const modalElement = modalRef?.current;
		if (!modalElement) return;
		Modal.getInstance(modalElement)?.hide();
		if (isFunction(setShown)) setShown(false);
	}, [setShown]);

	const onAppointmentSuccess = useCallback(
		(cb) => {
			const handleSuccess = () => {
				overlapSubmitBodyRef.current = null;
				setAppointmentState({});
				hideOverlapModal();
				if (isFunction(setShown)) setShown(false);
				if (isFunction(onSuccessCb)) onSuccessCb();
				if (isFunction(cb)) cb();
			};

			const modalElement = modalRef?.current;
			const isModalVisible = modalElement?.classList?.contains("show");

			if (isModalVisible) {
				modalElement?.addEventListener("hidden.bs.modal", handleSuccess, { once: true });
				Modal.getInstance(modalElement)?.hide();
			} else {
				handleSuccess();
			}
		},
		[onSuccessCb, setShown, hideOverlapModal]
	);

	const submitOverlapAppointment = useCallback(() => {
		const body = overlapSubmitBodyRef.current;
		if (!body || !Object.keys(body).length) return;

		setIsOverlapSubmitLoadingState(true);

		if (overlapSubmitRequestRef.current?.signal) overlapSubmitRequestRef.current.abort();
		overlapSubmitRequestRef.current = new AbortController();

		apiHandler(postPhysicianAppointment, {
			data: {
				body: { ...body, force_submit: true },
				options: {
					signal: overlapSubmitRequestRef.current.signal,
					headers: { "x-prevent-error-flashes": true },
				},
			},
			onError: ({ data: { is_overlapping = false } = {}, message = "" }) => {
				setIsOverlapSubmitLoadingState(false);
				if (is_overlapping) {
					setTimeOverlapMessageState(message);
					setIsTimeOverlapMessageEnabledState(true);
					return;
				}
				hideOverlapModal();
			},
			onSuccess: () => {
				hideOverlapModal();
				hideAppointmentModal();
				onAppointmentSuccess();
			},
		});
	}, [hideAppointmentModal, hideOverlapModal, onAppointmentSuccess]);

	// event handlers
	const onTimeOverlapError = (message = "", values = {}) => {
		overlapSubmitBodyRef.current = values;
		setAppointmentState(values);

		if (modalRef?.current) {
			modalRef.current?.addEventListener(
				"hidden.bs.modal",
				() => {
					if (message) setTimeOverlapMessageState(message);
					setIsTimeOverlapMessageEnabledState(true);
				},
				{ once: true }
			);
			Modal.getInstance(modalRef?.current)?.hide();
		}
	};

	const onTimeOverlapConfirm = () => {
		submitOverlapAppointment();
	};

	const onTimeOverlapCancel = () => {
		overlapSubmitBodyRef.current = null;
		setAppointmentState({});
		hideOverlapModal();
		hideAppointmentModal();
	};

	// effect hook
	useEffect(() => {
		const modalCurrentElement = modalRef?.current;
		if (!modalCurrentElement) return undefined;

		const onPreventUnwantedShow = (event) => {
			if (!isShown) {
				event.preventDefault();
				Modal.getInstance(modalCurrentElement)?.hide();
			}
		};

		let modal = Modal.getInstance(modalCurrentElement);
		if (!modal) {
			modal = new Modal(modalCurrentElement)[isShown ? "show" : "hide"]();
			modalCurrentElement?.addEventListener("hidden.bs.modal", () => isFunction(setShown) && setShown(false));
			modalCurrentElement?.addEventListener("show.bs.modal", () => isFunction(onShowCb) && onShowCb());
		} else {
			if (isShown) modal.show();
			if (!isShown) modal.hide();
		}

		modalCurrentElement.addEventListener("show.bs.modal", onPreventUnwantedShow);

		return () => {
			modalCurrentElement.removeEventListener("show.bs.modal", onPreventUnwantedShow);
			if (modal) Modal.getInstance(modalCurrentElement)?.dispose();
		};
	}, [isShown, setShown, onShowCb]);

	useEffect(() => {
		if (isObject(appointment)) setAppointmentState({ ...(appointment || {}) });
	}, [appointment]);

	useEffect(() => {
		return () => {
			if (overlapSubmitRequestRef.current?.signal) overlapSubmitRequestRef.current.abort();
		};
	}, []);

	return (
		portalsRoot &&
		ReactDOM.createPortal(
			<div
				className="modal fade"
				ref={modalRef}
				id={`appointmentModal-${modalId}`}
				tabIndex="-1"
				aria-labelledby={`appointmentModalLabel-${modalId}`}
				aria-hidden="true">
				<div className="modal-dialog modal-lg modal-dialog-centered">
					<div className="modal-content bg-white">
						<div className="modal-header border-0">
							<div className="hstack gap-2 align-items-center w-100">
								{!isTimeOverlapMessageEnabledState && (
									<span className="flex-shrink-0 text-primary">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="32"
											height="32"
											fill="currentColor"
											viewBox="0 0 17.499 17.492">
											<g transform="translate(-268.906 -392.543)">
												<path
													d="M268.906,406.994v-10.08a.556.556,0,0,0,.031-.094,3.364,3.364,0,0,1,3.1-2.916c.294-.02.589,0,.9,0,0-.23,0-.428,0-.626a.684.684,0,1,1,1.366,0c0,.2,0,.4,0,.6H281c0-.2,0-.4,0-.588a.685.685,0,1,1,1.366-.008c0,.2,0,.4,0,.607.208,0,.4,0,.582,0a3.374,3.374,0,0,1,3.446,3.33q.016,4.749,0,9.5a3.359,3.359,0,0,1-2.591,3.224c-.159.037-.32.066-.481.1H271.981a.566.566,0,0,0-.1-.029,3.381,3.381,0,0,1-2.9-2.614C268.957,407.26,268.933,407.127,268.906,406.994Zm16.129-7.7H270.272v.2q0,3.536,0,7.072a2.015,2.015,0,0,0,2.093,2.1q5.287,0,10.574,0a2.014,2.014,0,0,0,2.094-2.1q0-3.536,0-7.072Zm-.015-1.386a2.171,2.171,0,0,0-2.65-2.628c0,.425,0,.852,0,1.279a.683.683,0,1,1-1.365-.005c0-.376,0-.752,0-1.128,0-.054-.006-.108-.01-.165H274.3v.192c0,.376,0,.752,0,1.128a.682.682,0,1,1-1.364,0c-.005-.437,0-.874,0-1.309a2.171,2.171,0,0,0-2.645,2.634Z"
													transform="translate(0 0)"
												/>
												<path
													d="M281.6,435.633c.222,0,.444,0,.665,0a.681.681,0,0,1,.012,1.362q-.691.015-1.382,0a.681.681,0,0,1,.005-1.362C281.134,435.629,281.367,435.633,281.6,435.633Z"
													transform="translate(-7.966 -30.314)"
												/>
												<path
													d="M308.813,435.633c.222,0,.444,0,.665,0a.681.681,0,0,1,.007,1.363q-.682.012-1.365,0a.681.681,0,0,1,.011-1.363C308.358,435.629,308.586,435.633,308.813,435.633Z"
													transform="translate(-27.116 -30.314)"
												/>
												<path
													d="M308.817,422.036c.222,0,.444,0,.665,0a.682.682,0,0,1,0,1.363c-.449.007-.9.007-1.348,0a.682.682,0,1,1,0-1.364C308.362,422.033,308.59,422.036,308.817,422.036Z"
													transform="translate(-27.116 -20.748)"
												/>
												<path
													d="M281.59,423.4c-.222,0-.444,0-.665,0a.682.682,0,1,1,0-1.365q.665-.006,1.331,0a.682.682,0,1,1,0,1.364C282.034,423.4,281.812,423.4,281.59,423.4Z"
													transform="translate(-7.966 -20.746)"
												/>
												<path
													d="M295.186,422.037c.222,0,.444,0,.665,0a.681.681,0,0,1,.011,1.363q-.682.012-1.365,0a.682.682,0,0,1,.006-1.363C294.731,422.033,294.959,422.037,295.186,422.037Z"
													transform="translate(-17.53 -20.749)"
												/>
												<path
													d="M295.167,437c-.216,0-.432,0-.648,0a.691.691,0,0,1-.7-.691.683.683,0,0,1,.693-.674q.665-.007,1.33,0a.682.682,0,1,1,0,1.364C295.622,437,295.395,437,295.167,437Z"
													transform="translate(-17.53 -30.312)"
												/>
											</g>
										</svg>
									</span>
								)}
								<h3
									className="modal-title fs-6 vstack flex-grow-1 lh-1 mb-0"
									id={`appointmentModalLabel-${modalId}`}>
									<strong className="text-uppercase">
										{isTimeOverlapMessageEnabledState
											? "Heads up"
											: `${isEdit ? "Update" : ""} Schedule Appointment`}
									</strong>
									{!isTimeOverlapMessageEnabledState && (
										<span className="fw-light text-capitalize">
											<small>
												{[patient?.first_name || "", patient?.last_name || ""]
													.filter(Boolean)
													.join(" ")
													.trim() || "-"}
											</small>
										</span>
									)}
								</h3>
								<button
									type="button"
									className="btn-close ms-auto flex-shrink-0"
									data-bs-dismiss="modal"
									aria-label={t("actions.close")}></button>
							</div>
						</div>
						<div className="modal-body">
							<AppointmentForm
								patient={patient}
								onSuccess={onAppointmentSuccess}
								onTimeOverlapError={onTimeOverlapError}
								appointment={appointmentState}
								onChangePatientClickHandler={() => {
									modalRef?.current?.addEventListener(
										"hide.bs.modal",
										() => isFunction(setPatientShown) && setPatientShown(true),
										{ once: true }
									);
									Modal.getInstance(modalRef?.current).hide();
								}}
								onUploadNewDocumentClickHandler={() => {
									modalRef?.current?.addEventListener(
										"hide.bs.modal",
										() => setIsDocumentsShownState(true),
										{ once: true }
									);
									Modal.getInstance(modalRef?.current).hide();
								}}
								isSinglePatientView={isSinglePatientView}
								selectedDocuments={selectedDocuments}
								setSelectedDocuments={setSelectedDocuments}
								refetchDocuments={refetchDocumentsState}
								setRefetchDocuments={setRefetchDocumentsState}
								isEdit={isEdit}
								onDeleteButtonClickHandler={() => {
									modalRef?.current?.addEventListener(
										"hide.bs.modal",
										() => isFunction(onDeleteButtonClickHandler) && onDeleteButtonClickHandler(),
										{ once: true }
									);
									Modal.getInstance(modalRef?.current).hide();
								}}
							/>
							<TimeOverlappingModal
								ref={overlapModalRef}
								isShown={isTimeOverlapMessageEnabledState}
								setShown={setIsTimeOverlapMessageEnabledState}
								message={timeOverlapMessageState}
								onConfirm={onTimeOverlapConfirm}
								onCancel={onTimeOverlapCancel}
								isLoading={isOverlapSubmitLoadingState}
							/>
							<HcpShareDocumentModal
								patientId={patient?.id}
								onSuccess={(cb, uploadedDocuments) => {
									setRefetchDocumentsState(true);
									const incoming = Array.isArray(uploadedDocuments)
										? uploadedDocuments.filter(Boolean)
										: [uploadedDocuments].filter(Boolean);
									setSelectedDocuments((prev) => [...prev, ...incoming]);
									if (isFunction(cb)) cb(() => setShown(true));
								}}
								isShown={isDocumentsShownState}
								setShown={setIsDocumentsShownState}
							/>
						</div>
					</div>
				</div>
			</div>,
			portalsRoot
		)
	);
};

export default Appointment;
